import React, { useCallback, useState } from 'react';
import { useInView } from 'react-intersection-observer';
import { useSelector } from 'react-redux';

import { getMarket } from '@travel/i18n';
import { MOBILE_SCREEN, TABLET_SCREEN, useDeviceType } from '@travel/traveler-core/hooks';
import useRatBindClickComponent from '@travel/traveler-core/hooks/useRatBindClickComponent';
import { toSEOSearchString } from '@travel/traveler-core/utils';
import { FlatButton } from '@travel/ui';
import { cx, isNotEmptyArray } from '@travel/utils';

import Media from 'components/Media';
import NearbyTerminals from 'components/NearbyTerminals';
import Swiper from 'components/Swiper';

import { getLocation } from 'store/__router/selectors';
import { getLimitationsForCurrentMarket } from 'store/markets/selectors';

import { EMPTY_FUNCTION } from 'constants/defaultValue';
import { PROVIDER_CONTRACT_TYPES } from 'constants/provider';
import { RAT_PAGE_TYPE_OBJ } from 'constants/ratAnalytics';
import { Translate } from 'core/translate';
import { LinkWithRef } from 'core/universalRouter/Link/LinkWithRef';
import paths from 'core/universalRouter/paths';
import type { ProviderItem, SearchFormQuery } from 'ProviderList-Types';

import { getDataLayerPageName } from '../../utils';
import ProviderDetailsBox from './ProviderDetailsBox';
import ProviderHeaderBox, {
  BookmarkButton,
  ProviderReview,
  ProviderReviewPCListView,
  ProviderTitle,
} from './ProviderHeaderBox';
import ProviderPriceBox from './ProviderPriceBox';
import RoomUrgencyMessage from './RoomUrgencyMessage';

import styles from './providerBox.module.scss';

type RatParams = {
  'data-rat-cp': string;
  'data-rat-oa': string;
};

type Props = {
  /** data needed for RAT click event **/
  ratExtraParams?: RatParams;
  /** on what parent page this component lives */
  onPage: 'providerListUndated' | 'providerListDated';
  /** Object of provider data */
  item: ProviderItem;
  /** Object of current page query */
  query?: SearchFormQuery;
  /** Callback to be called when the provider item is hovered */
  onMouseOver?: (itemId: string) => void;
  /** Flag to define whether the component will be an expanded style or not */
  isExpandedMapStyle: boolean;
  /* index of the item */
  index: number;
  /* Indicates whether to hide the button for selecting room and plan options */
  shouldHideRoomAndPlanBtn?: boolean;
  /* custom class for image swiper */
  customSwiperStyle?: string;
};

function ProviderBox(props: Props) {
  const { item, onPage, index, ratExtraParams, shouldHideRoomAndPlanBtn = false } = props;

  const [isHovered, setIsHovered] = useState(false);
  const deviceType = useDeviceType();
  const isMobile = deviceType === MOBILE_SCREEN;
  const isTablet = deviceType === TABLET_SCREEN;
  const [ref, inView] = useInView({ triggerOnce: true });

  const node = React.useRef<HTMLAnchorElement>(null);
  useRatBindClickComponent(node);

  const market = useSelector(getMarket).marketCode;
  const location = useSelector(getLocation);
  const isNotEmptyMedia = isNotEmptyArray(item.media);
  const media = isNotEmptyMedia ? item.media[0] : null;
  const limitations = useSelector(getLimitationsForCurrentMarket);
  const isDirectContract = item.contractType === PROVIDER_CONTRACT_TYPES.DIRECT;

  const linkPath = `${paths.providerInfo.pathResolver(item.pathId)}${
    location?.search
      ? `?${toSEOSearchString(
          {
            startDate: props.query?.startDate,
            endDate: props.query?.endDate,
            noOfUnits: props.query?.noOfUnits,
            adults: props.query?.adults,
            childrenAges: props.query?.childrenAges,
            promotionCode: props.query?.promotionCode,
            isCouponSpecified: props.query?.isCouponSpecified,
            salesPromotionId: props.query?.salesPromotionId,
            features: props.query?.features,
            freeCancellation: props.query?.freeCancellation,
          },
          limitations,
        )}`
      : ''
  }`;

  const { parentName } = RAT_PAGE_TYPE_OBJ[onPage];

  const ROOM_AVAILABILITY_CHECK = 10;
  const shouldShowUrgencyMessage =
    props.query?.startDate &&
    props.query?.endDate &&
    item.availabilityCount &&
    item.availabilityCount < ROOM_AVAILABILITY_CHECK;

  const onFocus = useCallback(() => {
    setIsHovered(true);
  }, []);

  const onBlur = useCallback((_event: React.FocusEvent) => {
    if (!_event.currentTarget.contains(_event.relatedTarget as Node)) {
      setIsHovered(false);
    }
  }, []);

  return (
    <article
      data-ratid={`provider_${props.item.id}`}
      data-testid={`providerBox-article-${props.item.id}`}
      data-ratevent="appear"
      data-ratparam="all"
      data-rat-page_name={parentName}
      data-rat-shop_id={props.item.id}
      className={styles.container}
      onMouseOver={() => props.onMouseOver?.(props.item.id)}
      onFocus={EMPTY_FUNCTION}
    >
      <LinkWithRef
        to={linkPath}
        className={cx(styles.itemBoxWrapper, isDirectContract && styles.topBorder)}
        onMouseEnter={() => setIsHovered(true)}
        onMouseLeave={() => setIsHovered(false)}
        onFocus={onFocus}
        onBlur={onBlur}
        data-rat-abtest="NA"
        data-rat-cv={`{ 'item_view': ${index + 1} }`}
        data-rat-itemid={item.id}
        data-rat-pgn={getDataLayerPageName(props.query)}
        data-rat-pgt="search"
        data-rat-price={item.price?.total}
        data-rat-tag={item.features.map((f) => f.text).join(', ')}
        data-ratid={`providerBox-link-${props.item.id}`}
        data-ratevent="click"
        data-testid={`providerBox-link-${props.item.id}`}
        ref={node}
        {...ratExtraParams}
      >
        <div
          data-testid={`providerBox-article-${props.item.id}-content`}
          className={cx(styles.itemDetails, props.isExpandedMapStyle && styles.expanded)}
        >
          <div className={styles.providerMediaWrapper}>
            {/* image */}
            {item.media?.length > 1 ? (
              isMobile ? (
                <Swiper
                  className={cx(styles.galleryDetails, props.customSwiperStyle)}
                  paginationType="swipe"
                  shouldRenderAllChild={false}
                >
                  {item.media.map((media, i) => (
                    <Media
                      key={media.url}
                      wrapperClassName={styles.mediaWrapper}
                      media={media}
                      sizes="100vw"
                      isMediaOnly={true}
                      isDisableLazyload={i === 0 && index < 2}
                    />
                  ))}
                </Swiper>
              ) : (
                <Swiper
                  className={cx(styles.galleryDetails, props.isExpandedMapStyle && styles.expanded)}
                  paginationType="arrows"
                  isResizeDependency={props.isExpandedMapStyle}
                  paginationCallback={(event) => {
                    event.preventDefault();
                    event.stopPropagation();
                  }}
                  shouldHideArrows={isTablet ? false : !isHovered}
                  isSmallArrows={isTablet}
                  shouldRenderAllChild={false}
                >
                  {item.media.map((media, i) => (
                    <Media
                      key={media.id}
                      wrapperClassName={cx(
                        styles.mediaWrapper,
                        styles.nonMobileContainerFill,
                        props.isExpandedMapStyle && styles.expanded,
                      )}
                      media={media}
                      sizes={isTablet ? '15vw' : '20vw'}
                      isMediaOnly={true}
                      isDisableLazyload={i === 0 && index < 6}
                    />
                  ))}
                </Swiper>
              )
            ) : (
              <Media
                wrapperClassName={cx(
                  styles.galleryDetails,
                  styles.mediaWrapper,
                  props.isExpandedMapStyle && styles.expanded,
                )}
                media={media}
                sizes={isMobile ? '100vw' : isTablet ? '15vw' : '20vw'}
                isMediaOnly={true}
              />
            )}
            {(isMobile || !props.isExpandedMapStyle) && (
              <BookmarkButton
                className={styles.bookmark}
                providerId={item.id}
                isBookmarked={item.isBookmarked ?? false}
              />
            )}

            {isMobile && (
              <div className={styles.providerTitleBox}>
                <ProviderTitle item={item} className={styles.titleOnImage} />
                {item.nearbyTerminal && <NearbyTerminals {...item.nearbyTerminal} />}
              </div>
            )}
          </div>
          {/* detail */}

          <div className={cx(styles.infoBox, props.isExpandedMapStyle && styles.expanded)}>
            <div className={styles.infoContentBox}>
              <div className={styles.infoHotelContentBox}>
                <ProviderHeaderBox item={item} market={market} />

                {!isMobile && (
                  <ProviderTitle
                    item={item}
                    className={props.isExpandedMapStyle ? styles.itemTitleInMapView : ''}
                  />
                )}
                {props.isExpandedMapStyle && <ProviderReview item={item} market={market} />}
                <ProviderDetailsBox onPage={onPage} item={item} deviceType={deviceType} />
                {shouldShowUrgencyMessage && item.availabilityCount && (
                  <div ref={ref}>
                    <RoomUrgencyMessage
                      availabilityCount={item.availabilityCount}
                      tagClassName={cx(styles.hideTag, inView && styles.animation)}
                    />
                  </div>
                )}
              </div>
              <div
                className={
                  isDirectContract
                    ? styles.expandedActionBox
                    : cx(styles.expandedActionBox, styles.expandedActionBoxIndirect)
                }
                data-testid="providerBox-priceBox-wrapper"
              >
                {!isMobile &&
                  (props.isExpandedMapStyle ? (
                    <BookmarkButton
                      iconStyle="gold"
                      className={styles.bookmark}
                      providerId={item.id}
                      isBookmarked={item.isBookmarked ?? false}
                    />
                  ) : (
                    <ProviderReviewPCListView item={item} market={market} />
                  ))}
                <ProviderPriceBox
                  className={styles.priceBox}
                  item={item}
                  query={props.query}
                  isExpandedMapStyle={props.isExpandedMapStyle}
                />
              </div>
            </div>
          </div>
        </div>
        {!shouldHideRoomAndPlanBtn && (
          <div
            className={styles.seeRoomAndPlanBtn}
            data-testid="providerBox-seeRoomAndPlanBtn-button"
          >
            <FlatButton className={styles.btn} type="button" isSmallerButtonByDefault>
              <Translate id="Provider_List.Search_Results.Call_To_Action_Button" />
            </FlatButton>
          </div>
        )}
      </LinkWithRef>
    </article>
  );
}

export default ProviderBox;
